import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'
import { API_HOST, ApiError } from '../api/client'
import { useChangelog, useDoors, useEdgeGateways, useSystems } from '../api/hooks'
import { useConsoleLive } from '../hooks/useConsoleLive'
import { initials } from '../lib/format'
import { StatusDot } from './ui'
import { CustomerSwitcher } from './CustomerSwitcher'
import { IdleTimeout } from './IdleTimeout'
import { UpdateBanner } from './UpdateBanner'
import { OstrelMark } from './OstrelMark'
import { OpsTailDrawer } from './OpsTailDrawer'
import { hasUnread, useChangelogStore } from '../stores/changelog'

const APP_VERSION = __APP_VERSION__

export function AppShell() {
  const customer = useAuthStore((s) => s.customer)
  const principal = useAuthStore((s) => s.principal)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()

  // Mobile nav: the sidebar is a drawer below md (hamburger in the header,
  // backdrop tap or any navigation closes it), persistent from md up.
  const [navOpen, setNavOpen] = useState(false)
  const { pathname } = useLocation()
  useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  const systems = useSystems()
  const doors = useDoors()
  const gateways = useEdgeGateways()
  const live = useConsoleLive()
  const changelog = useChangelog()
  const seenVersion = useChangelogStore((s) => s.seen)
  const changelogUnread = hasUnread(changelog.data?.entries[0]?.version, seenVersion)

  // Boot gate: the shell's first API load failing on a network error or a
  // 502/503 means the API is mid-deploy (Caddy answers a JSON 503 in the
  // deploy window) — show a branded retrying state and poll until the API
  // answers, instead of a broken shell full of error cards.
  const bootFailure =
    systems.isError &&
    (systems.error instanceof ApiError
      ? systems.error.status === 502 || systems.error.status === 503
      : true)
  const { refetch } = systems
  useEffect(() => {
    if (!bootFailure) return
    const t = window.setInterval(() => refetch(), 5000)
    return () => window.clearInterval(t)
  }, [bootFailure, refetch])
  if (bootFailure) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-ostrel-navy">
        <OstrelMark size={40} />
        <p className="font-display text-2xl text-ostrel-light">ostrel</p>
        <p className="flex items-center gap-2 text-sm text-ostrel-muted">
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-ostrel-gold" />
          Ostrel is updating — retrying…
        </p>
      </div>
    )
  }

  const systemCount = systems.data?.length
  // Badges mean "needs attention", not totals: systems not yet fully active
  // (draft → verify, needs_sites → pick sites, auth_failed → reauthenticate).
  const systemIssueCount = systems.data?.filter((s) => s.status !== 'active').length
  // Same attention-only convention: a gateway needs attention when it is
  // disconnected (never enrolled, or the appliance dropped its socket).
  const disconnectedGatewayCount = gateways.data?.filter((g) => !g.connected).length
  const doorCount = doors.data?.length

  const operatorName =
    principal && (principal.first_name || principal.last_name)
      ? `${principal.first_name} ${principal.last_name}`.trim()
      : (principal?.username ?? '')

  // Grouped by what the operator is working on; within a section, most-used
  // first. What's new sits apart, above the profile.
  const navSections: { title: string; items: NavItem[] }[] = [
    {
      title: 'Overview',
      items: [
        { code: 'DSH', label: 'Dashboard', to: '/' },
        { code: 'EVT', label: 'Events', to: '/events' },
      ],
    },
    {
      title: 'Directory',
      items: [
        { code: 'PPL', label: 'People', to: '/directory' },
        { code: 'GRP', label: 'Groups', to: '/groups' },
        { code: 'CRD', label: 'Credentials', to: '/credentials' },
      ],
    },
    {
      title: 'Access',
      items: [
        { code: 'ACC', label: 'Access groups', to: '/access-groups' },
        { code: 'SCH', label: 'Schedules', to: '/schedules' },
        { code: 'DRC', label: 'Door codes', to: '/door-codes' },
      ],
    },
    {
      title: 'Places',
      items: [
        { code: 'LOC', label: 'Locations', to: '/locations' },
        { code: 'ZON', label: 'Zones', to: '/zones' },
        { code: 'DOR', label: 'Doors', to: '/doors' },
        { code: 'DEV', label: 'Devices', to: '/devices' },
      ],
    },
    {
      title: 'Integrations',
      items: [
        { code: 'SYS', label: 'Systems', to: '/systems', badge: systemIssueCount },
        { code: 'GTW', label: 'Gateways', to: '/gateways', badge: disconnectedGatewayCount },
        { code: 'WHK', label: 'Webhooks', to: '/webhooks' },
      ],
    },
    {
      title: 'Administration',
      items: [
        { code: 'USR', label: 'Operators', to: '/operators' },
        { code: 'AUD', label: 'Audit trail', to: '/audit' },
        ...(customer?.role === 'admin' ? [{ code: 'TRH', label: 'Trash', to: '/trash' }] : []),
      ],
    },
  ]
  const whatsNew: NavItem = { code: 'NEW', label: "What's new", to: '/whats-new', dot: changelogUnread }

  return (
    <div className="flex min-h-screen bg-ostrel-light text-ostrel-ink">
      {navOpen && (
        <div
          aria-hidden
          className="fixed inset-0 z-30 bg-ostrel-navy/70 md:hidden"
          onClick={() => setNavOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-[260px] shrink-0 flex-col bg-ostrel-navy transition-transform md:sticky md:top-0 md:z-auto md:h-screen md:w-[230px] md:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <Link to="/" className="flex items-center gap-2 focus:outline-none focus:ring-1 focus:ring-ostrel-gold">
            <OstrelMark size={22} />
            {/* The wordmark stays 'ostrel'; the product subbrand rides
                under it in the dim token (Ostrel Network comes later). */}
            <span className="flex flex-col">
              <span className="font-display text-2xl leading-none text-ostrel-light">ostrel</span>
              <span className="mt-1 text-[9px] font-semibold uppercase tracking-widest text-ostrel-dim">
                security
              </span>
            </span>
          </Link>
          <span className="flex items-center gap-2">
            <span className="rounded-sm border border-ostrel-border px-1 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-ostrel-dim">
              Ops
            </span>
            <button
              type="button"
              aria-label="Close navigation"
              onClick={() => setNavOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-sm text-ostrel-dim transition-colors hover:text-ostrel-light focus:outline-none focus:ring-1 focus:ring-ostrel-gold md:hidden"
            >
              ✕
            </button>
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto px-3">
          {navSections.map((section, i) => (
            <div key={section.title}>
              <p
                className={`px-2 pb-1.5 text-[9px] font-semibold uppercase tracking-widest text-ostrel-dim ${
                  i === 0 ? 'pt-2' : 'pt-4'
                }`}
              >
                {section.title}
              </p>
              <ul className="flex flex-col gap-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavItemLink item={item} />
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <p className="px-2 pt-5 pb-2 text-[9px] font-semibold uppercase tracking-widest text-ostrel-dim">
            Connected systems
          </p>
          <ul className="flex flex-col gap-0.5">
            {(systems.data ?? []).map((s) => (
              <li key={s.unique_id}>
                <Link
                  to={`/systems/${s.unique_id}`}
                  className="flex items-center gap-2 rounded-sm px-2 py-1 text-xs text-ostrel-muted transition-colors hover:bg-ostrel-raised hover:text-ostrel-light focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                >
                  <StatusDot status={s.status} />
                  <span className="truncate">{s.name}</span>
                </Link>
              </li>
            ))}
            {systems.data?.length === 0 && (
              <li className="px-2 py-1 text-xs text-ostrel-dim">No systems</li>
            )}
          </ul>
        </nav>

        <div className="border-t border-ostrel-border px-3 pt-2">
          <NavItemLink item={whatsNew} />
        </div>
        <div className="flex items-center px-3 pb-2">
          <Link
            to="/profile"
            className="flex min-w-0 flex-1 items-center gap-2.5 rounded-sm px-2 py-2 transition-colors hover:bg-ostrel-raised focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
          >
            <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-ostrel-border bg-ostrel-raised text-[11px] font-semibold text-ostrel-gold">
              {initials(operatorName || '?')}
              <span className="absolute -right-0.5 -bottom-0.5 h-1.5 w-1.5 rounded-full bg-ostrel-ok" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm text-ostrel-light">{operatorName}</span>
              <span className="block truncate text-[9px] font-semibold uppercase tracking-widest text-ostrel-dim">
                {customer?.role ?? ''}
              </span>
            </span>
          </Link>
          <button
            onClick={() => {
              logout()
              navigate('/login')
            }}
            title="Sign out"
            aria-label="Sign out"
            className="rounded-sm px-2 py-1 text-xs text-ostrel-dim transition-colors hover:text-ostrel-danger focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
          >
            ⏻
          </button>
        </div>
        <div className="flex items-center justify-between border-t border-ostrel-border px-5 py-2.5 font-mono text-[9px] text-ostrel-dim">
          <span className="flex items-center gap-1.5">
            <span
              title={live === 'connected' ? 'live' : 'reconnecting…'}
              className={`inline-block h-1.5 w-1.5 rounded-full ${
                live === 'connected' ? 'animate-pulse bg-ostrel-ok' : 'bg-ostrel-dim'
              }`}
            />
            {API_HOST}
          </span>
          <span className="flex items-center gap-1.5">
            <OpsTailDrawer live={live} />
            <Link
              to="/whats-new"
              title={`Console ${APP_VERSION} · API ${changelog.data?.version ?? '…'}`}
              className="transition-colors hover:text-ostrel-light focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
            >
              {changelog.data?.version ?? APP_VERSION}
            </Link>
          </span>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-ostrel-line bg-ostrel-light px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <button
              type="button"
              aria-label="Open navigation"
              aria-expanded={navOpen}
              onClick={() => setNavOpen(true)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-ostrel-line bg-ostrel-card text-ostrel-ink transition-colors hover:bg-ostrel-sand focus:outline-none focus:ring-2 focus:ring-ostrel-gold md:hidden"
            >
              ☰
            </button>
            <CustomerSwitcher />
            <p className="tnum flex items-center gap-1.5 text-xs text-ostrel-ink-muted">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-ostrel-ok" />
              {systemCount ?? '–'} systems · {doorCount ?? '–'} doors
            </p>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
      <IdleTimeout />
      <UpdateBanner />
    </div>
  )
}

interface NavItem {
  code: string
  label: string
  to: string
  badge?: number
  dot?: boolean
}

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ostrel-gold ${
          isActive
            ? 'bg-ostrel-raised text-ostrel-light shadow-[inset_2px_0_0_0_var(--color-ostrel-gold)]'
            : 'text-ostrel-muted hover:bg-ostrel-raised hover:text-ostrel-light'
        }`
      }
    >
      <span className="tnum w-7 text-[9px] font-semibold uppercase tracking-wider text-ostrel-dim">{item.code}</span>
      <span className="flex-1">{item.label}</span>
      {item.dot && <span aria-label="Unread" title="Unread" className="h-1.5 w-1.5 rounded-full bg-ostrel-gold" />}
      {item.badge !== undefined && item.badge > 0 && (
        <span className="tnum rounded-sm border border-ostrel-gold/40 bg-ostrel-gold/10 px-1 text-[10px] text-ostrel-gold">
          {item.badge}
        </span>
      )}
    </NavLink>
  )
}
